import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Text,
  Stack,
  Image,
  Divider,
  Badge,
  Flex,
  Icon,
  Link,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FiUser,
  FiShoppingBag,
  FiLogOut,
  FiGrid,
  FiCalendar,
  FiStar,
  FiPackage,
} from "react-icons/fi";
import { useEffect, useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import Loading from "../Errorandloading/Loading";

export default function OrderDetails() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const cardPadding = useBreakpointValue({ base: 4, md: 8 });

  useEffect(() => {
    const fetchOrder = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await axiosInstance.get(`/api/orders/${orderId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setOrder(res.data);
      } catch (err) {
        console.error("Failed to load order", err);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (!order) return <Loading />;

  const formatDate = (date) =>
    new Date(date).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <Box minH="100vh" bg="#F2EEE8" p={{ base: 4, md: 28 }} py={{ base: 24 }}>
      {/* Header */}
      <Flex align="center" mb={6} p={4}>
        <Box
          bg="#b38100"
          color="white"
          fontWeight="bold"
          borderRadius="full"
          boxSize="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr={3}
          fontSize="lg"
          textTransform="uppercase"
        >
          {order?.shippingAddress?.name?.charAt(0) || "U"}
        </Box>
        <Text fontSize="2xl" fontWeight="bold">
          My Account
        </Text>
      </Flex>

      <Flex direction={{ base: "column", md: "row" }} gap={6}>
        {/* Sidebar */}
        <Box w={{ base: "full", md: "260px" }} bg="#F2EEE8" p={4}>
          <Stack spacing={6}>
            <NavItem icon={FiGrid} label="Over View" to="/overview" />
            <NavItem
              icon={FiShoppingBag}
              label="Order History"
              to="/order"
              fontWeight="bold"
            />
            <NavItem icon={FiCalendar} label="My Appointment" to="/myappointment" />
            <NavItem icon={FiPackage} label="Track My Order" to="/track" />
            <NavItem icon={FiUser} label="My Profile" to="/myprofile" />
            <NavItem icon={FiStar} label="Points" to="/points" />
            <NavItem icon={FiLogOut} label="Log out" to="/logout" />
          </Stack>
        </Box>

        {/* Main Content */}
        <Box flex="1" bg="#F2EEE8" p={4}>
          <Box
            bg="white"
            borderRadius="md"
            boxShadow="md"
            p={cardPadding}
            maxW="900px"
            mx="auto"
          >
            {/* Title */}
            <Text fontSize="2xl" fontWeight="bold" mb={6}>
              Order Details
            </Text>

            {/* Order Summary */}
            <Stack spacing={3} mb={6}>
              <Text fontSize="lg">
                <strong>Order ID:</strong>{" "}
                <Text as="span" color="gray.600">
                  {order._id.toUpperCase()}
                </Text>
              </Text>

              <Text fontSize="lg">
                <strong>Status:</strong>{" "}
                <Badge
                  colorScheme={
                    order.status === "paid"
                      ? "green"
                      : order.status === "Delivered"
                      ? "blue"
                      : "yellow"
                  }
                >
                  {order.status.toUpperCase()}
                </Badge>
              </Text>

              <Text fontSize="lg">
                <strong>Total Amount:</strong> ₹{order.amount}
              </Text>

              <Text fontSize="sm" color="gray.500">
                Placed on: {formatDate(order.createdAt)}
              </Text>

              {order.expectedArrival && (
                <Text fontSize="sm" color="gray.600">
                  Expected Arrival: {formatDate(order.expectedArrival)}
                </Text>
              )}
            </Stack>

            <Divider my={4} />

            {/* Shipping Address */}
            <Box mb={6}>
              <Text fontWeight="bold" fontSize="xl" mb={2}>
                Shipping Address
              </Text>
              <Stack spacing={1}>
                <Text>{order.shippingAddress.name}</Text>
                <Text>
                  {order.shippingAddress.addressLine1},{" "}
                  {order.shippingAddress.addressLine2}
                </Text>
                <Text>
                  {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                  {order.shippingAddress.postalCode}
                </Text>
                <Text>{order.shippingAddress.country}</Text>
              </Stack>
            </Box>

            <Divider my={4} />

            {/* Items */}
            <Box>
              <Text fontWeight="bold" fontSize="xl" mb={4}>
                Items in this Order
              </Text>

              {order.items.map((item) => (
                <Box
                  key={item._id}
                  display="flex"
                  alignItems="flex-start"
                  gap={4}
                  mb={6}
                  border="1px solid"
                  borderColor="gray.100"
                  p={4}
                  rounded="md"
                  bg="gray.50"
                >
                  <Image
                    src={item.image}
                    alt={item.name}
                    boxSize={{ base: "80px", md: "100px" }}
                    objectFit="cover"
                    borderRadius="md"
                  />
                  <Box>
                    <Text fontWeight="semibold" fontSize="lg" mb={1}>
                      {item.name}
                    </Text>

                    <Text fontSize="sm" color="gray.600">
                      Price: ₹{item.price}
                    </Text>

                    <Text fontSize="sm" color="gray.600">
                      Quantity: {item.quantity}
                    </Text>

                    {item.metalType && item.clarity && item.size && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Metal: {item.metalType}, Clarity: {item.clarity}, Size:{" "}
                        {item.size}
                      </Text>
                    )}

                    <Text fontWeight="medium" mt={2}>
                      Total: ₹{item.price * item.quantity}
                    </Text>

                    {/* Personalization */}
                    {item.personalization && (
                      <Box
                        mt={3}
                        p={3}
                        bg="white"
                        border="1px dashed gray"
                        borderRadius="md"
                      >
                        <Text fontWeight="bold" fontSize="sm" mb={1}>
                          Personalization
                        </Text>
                        {item.personalization.engraving && (
                          <Text fontSize="sm" color="gray.700">
                            <strong>Engraving:</strong>{" "}
                            {item.personalization.engraving}
                          </Text>
                        )}
                        {item.personalization.birthStone && (
                          <Text fontSize="sm" color="gray.700">
                            <strong>Birthstone:</strong>{" "}
                            {item.personalization.birthStone}
                          </Text>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

// Sidebar NavItem
function NavItem({ icon, label, to = "#", fontWeight = "medium" }) {
  return (
    <Link
      as={RouterLink}
      to={to}
      _hover={{ textDecoration: "underline", color: "black" }}
      display="flex"
      alignItems="center"
      gap={3}
      color="gray.700"
    >
      <Icon as={icon} boxSize={5} />
      <Text fontWeight={fontWeight}>{label}</Text>
    </Link>
  );
}
